//  app/components/Plugins/Sentiment/Sentiment.tsx:
'use client'

import { useState } from 'react'

/**
 * @uiComponent
 * @name Sentiment
 * @description The Sentiment component provides comprehensive sentiment analysis for stocks using multiple data sources.
 * It analyzes news articles, Twitter posts, and Reddit discussions to generate overall sentiment scores and detailed breakdowns.
 * Features include real-time sentiment scoring, source-specific analysis, and visual sentiment indicators with color-coded results.
 * @location Sentiment plugin in the trading terminal
 * @prop none - No external props, manages internal state for stock input and sentiment data
 */

/**
 * @uiWorkflow
 * @name Analyze Stock Sentiment from Multiple Sources
 * @description How to get comprehensive sentiment analysis for any stock using news, social media, and forums
 * @platform sentiment
 * @step 1 Navigate to the Sentiment Analysis plugin [component: Sentiment] [action: navigate]
 * @step 2 Enter a stock symbol or company name [component: Sentiment] [action: type] [notes: e.g., TCS, AAPL, Tesla]
 * @step 3 Click "Analyze Sentiment" to start analysis [component: Sentiment] [action: click]
 * @step 4 Wait for sentiment data to load [component: Sentiment] [action: wait] [notes: Fetches from news, Twitter, Reddit]
 * @step 5 Review the overall sentiment score [component: Sentiment] [action: observe] [notes: Positive, Negative, or Neutral]
 * @step 6 Examine the sentiment breakdown [component: Sentiment] [action: analyze] [notes: Total score, positive/negative/neutral counts]
 * @step 7 Check data source distribution [component: Sentiment] [action: review] [notes: News, Twitter, Reddit source counts]
 * @step 8 Scroll down to detailed news analysis [component: Sentiment] [action: examine]
 * @step 9 Read individual article sentiments [component: Sentiment] [action: study] [notes: Article-by-article breakdown]
 * @step 10 Use insights for investment decisions [component: Sentiment] [action: strategize]
 */

/**
 * @uiWorkflow
 * @name Examine Detailed News Article Sentiment
 * @description How to analyze individual news articles and their sentiment contributions
 * @platform sentiment
 * @step 1 Complete a sentiment analysis [component: Sentiment] [action: prerequisite] [notes: Run analysis first]
 * @step 2 Scroll to the "Detailed News Analysis" section [component: Sentiment] [action: navigate]
 * @step 3 Review each article's sentiment score [component: Sentiment] [action: examine] [notes: Positive/negative numerical scores]
 * @step 4 Read article headlines and summaries [component: Sentiment] [action: read] [notes: Understand context behind scores]
 * @step 5 Note the color coding of articles [component: Sentiment] [action: observe] [notes: Green=positive, red=negative, gray=neutral]
 * @step 6 Identify most influential articles [component: Sentiment] [action: prioritize] [notes: Highest positive/negative scores]
 * @step 7 Click through to read full articles [component: Sentiment] [action: research] [notes: External links if available]
 * @step 8 Compare article sentiment with overall score [component: Sentiment] [action: correlate]
 * @step 9 Look for sentiment trends over time [component: Sentiment] [action: pattern-match]
 */

/**
 * @uiWorkflow
 * @name Compare Sentiment Across Different Stocks
 * @description How to analyze and compare sentiment for multiple stocks
 * @platform sentiment
 * @step 1 Start with your first stock analysis [component: Sentiment] [action: baseline] [notes: Record the sentiment results]
 * @step 2 Note the overall sentiment and scores [component: Sentiment] [action: record]
 * @step 3 Clear the input and enter a second stock [component: Sentiment] [action: change] [notes: Different symbol or company]
 * @step 4 Run sentiment analysis for the second stock [component: Sentiment] [action: analyze]
 * @step 5 Compare overall sentiment classifications [component: Sentiment] [action: compare] [notes: Positive vs Negative vs Neutral]
 * @step 6 Compare total sentiment scores [component: Sentiment] [action: evaluate] [notes: Higher scores = more positive]
 * @step 7 Analyze source distribution differences [component: Sentiment] [action: contrast] [notes: News vs social media coverage]
 * @step 8 Identify which stock has better sentiment [component: Sentiment] [action: rank]
 * @step 9 Use comparative analysis for portfolio decisions [component: Sentiment] [action: decide]
 * @step 10 Monitor sentiment changes over time [component: Sentiment] [action: track]
 */

export default function SentimentPage() {
  const [stock, setStock] = useState('TCS')
  const [data, setData] = useState<any>(null)
  const [loading, setLoading] = useState(false)

  async function fetchSentiment() {
    setLoading(true)
    try {
      const res = await fetch(
        `/api/sentiment/analyze?stock=${encodeURIComponent(stock)}`
      )
      const json = await res.json()
      setData(json)
    } catch (error) {
      console.error('Error fetching sentiment data:', error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div style={{ padding: '1rem', color: 'black', maxWidth: '800px' }}>
      <h1>Sentiment Analysis for Stocks</h1>
      <input
        type="text"
        value={stock}
        style={{ color: '#88a1ac' }}
        onChange={e => setStock(e.target.value)}
        placeholder="Enter stock symbol or name"
      />
      <button onClick={fetchSentiment} disabled={loading}>
        {loading ? 'Loading...' : 'Analyze Sentiment'}
      </button>

      {data && (
        <div style={{ marginTop: '1rem' }}>
          {/* Existing summary section */}
          <h2>Stock: {data.stock}</h2>
          <div
            className="sentiment-summary"
            style={{
              padding: '1rem',
              backgroundColor:
                data.sentiment === 'Positive'
                  ? '#e6ffe6'
                  : data.sentiment === 'Negative'
                    ? '#ffe6e6'
                    : '#f0f0f0',
              borderRadius: '8px',
              marginBottom: '1rem'
            }}
          >
            <h3>
              <strong>Overall Sentiment:</strong> {data.sentiment}
            </h3>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                gap: '1rem'
              }}
            >
              <div>Total Score: {data.details.totalScore}</div>
              <div style={{ color: 'green' }}>
                Positive: {data.details.positiveCount}
              </div>
              <div style={{ color: 'red' }}>
                Negative: {data.details.negativeCount}
              </div>
              <div style={{ color: 'gray' }}>
                Neutral: {data.details.neutralCount}
              </div>
            </div>
          </div>
          <p>
            <small>
              Sources - News: {data.sources.news}, Twitter:{' '}
              {data.sources.twitter}, Reddit: {data.sources.reddit}
            </small>
          </p>

          {/* New sections */}
          <div className="news-analysis">
            <h3>Detailed News Analysis</h3>
            {data.articles &&
              data.articles.map((article: any, index: number) => (
                <div
                  key={index}
                  style={{
                    padding: '1rem',
                    margin: '0.5rem 0',
                    backgroundColor:
                      article.sentiment > 0
                        ? '#f0fff0'
                        : article.sentiment < 0
                          ? '#fff0f0'
                          : '#f8f8f8',
                    borderLeft: `4px solid ${
                      article.sentiment > 0
                        ? '#4caf50'
                        : article.sentiment < 0
                          ? '#f44336'
                          : '#9e9e9e'
                    }`,
                    borderRadius: '4px'
                  }}
                >
                  <h4 style={{ margin: '0 0 0.5rem 0' }}>{article.title}</h4>
                  <p style={{ margin: '0.5rem 0', fontSize: '0.9rem' }}>
                    {article.description}
                  </p>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      fontSize: '0.8rem',
                      color: '#666'
                    }}
                  >
                    <span>Sentiment Score: {article.sentiment}</span>
                    <span>Source: {article.source}</span>
                    <span>
                      {new Date(article.publishedAt).toLocaleDateString()}
                    </span>
                    <a
                      href={article.url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Read More
                    </a>
                  </div>
                </div>
              ))}
          </div>

          <div className="sentiment-trends" style={{ marginTop: '2rem' }}>
            <h3>Sentiment Trends</h3>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: '1rem',
                marginTop: '1rem'
              }}
            >
              <div
                className="trend-card"
                style={{
                  padding: '1rem',
                  backgroundColor: '#f8f8f8',
                  borderRadius: '8px'
                }}
              >
                <h4>24 Hour Trend</h4>
                <div
                  style={{
                    fontSize: '1.5rem',
                    fontWeight: 'bold',
                    color: data.trends?.daily > 0 ? 'green' : 'red'
                  }}
                >
                  {data.trends?.daily > 0 ? '↑' : '↓'}{' '}
                  {Math.abs(data.trends?.daily || 0)}%
                </div>
              </div>
              <div
                className="trend-card"
                style={{
                  padding: '1rem',
                  backgroundColor: '#f8f8f8',
                  borderRadius: '8px'
                }}
              >
                <h4>News Volume</h4>
                <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
                  {data.sources.news} articles
                </div>
              </div>
              <div
                className="trend-card"
                style={{
                  padding: '1rem',
                  backgroundColor: '#f8f8f8',
                  borderRadius: '8px'
                }}
              >
                <h4>Sentiment Strength</h4>
                <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
                  {Math.abs(
                    data.details.totalScore /
                      (data.details.positiveCount +
                        data.details.negativeCount || 1)
                  ).toFixed(2)}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
